import { createSelector } from 'reselect';
import memoize from 'lodash/memoize';

import { createParameterSelector, OperationStatus } from '@pressreader/appstore';
import { selectGetConfigValue } from '@pressreader/config';
import { defaultIssueContentRestrictions, defaultPublicationContentRestrictions, IssueContentRestrictions } from '@pressreader/content-types';
import { extractCid } from '@pressreader/content-utils';
import { error } from '@pressreader/logger';
import { forOwn, hasOwnProperty } from '@pressreader/utils';

import { getIssueState } from '../reducers';

import { selectIssueInfoByCidDateKey } from './issueinfo';

const getRestrictionsState = createSelector(getIssueState, state => state.restrictions);

const getConfigRestrictions = createSelector(selectGetConfigValue, getConfigValue => ({
    comments: getConfigValue<boolean>('ui.Comments.enable', true) && getConfigValue<boolean>('features.comments', false),
}));

const selectRadioAvailableLocales = createSelector(selectGetConfigValue, getConfigValue => {
    const { languages } = getConfigValue<{ languages: string[] }>('radio', { languages: [] });
    return languages;
});

const selectPublicationsRestrictions = createSelector(getRestrictionsState, state => state.publications);

const selectRestrictionsByCidSelector = createSelector(selectPublicationsRestrictions, state => (cid: string) => {
    if (state.status === OperationStatus.Done && state.restrictions[cid]) {
        return state.restrictions[cid];
    }

    return defaultPublicationContentRestrictions;
});

const selectIssuesRestrictionsMap = createSelector(getRestrictionsState, state => state.issues);

const selectCidDateKey = createParameterSelector<'cidDateKey', string | undefined>('cidDateKey');
const selectIssueLanguage = createParameterSelector<'language', string>('language');

const selectIssueRestrictionsByCidDateKeySelector = createSelector(
    getConfigRestrictions,
    selectRestrictionsByCidSelector,
    selectIssuesRestrictionsMap,
    (configRestrictions, restrictionsByCid, issuesRestrictions) =>
        memoize((cidDateKey: string) => {
            const cid = extractCid(cidDateKey);

            const publicationRestrictions = restrictionsByCid(cid);
            let issueRestrictions: IssueContentRestrictions = { ...defaultIssueContentRestrictions };

            if (issuesRestrictions[cidDateKey]?.status === OperationStatus.Done) {
                issueRestrictions = issuesRestrictions[cidDateKey].restrictions;
            }

            // publication restrictions have more priority then issue-level restrictions
            forOwn(publicationRestrictions, (restriction, key) => {
                if (restriction) {
                    if (hasOwnProperty(issueRestrictions, key)) {
                        issueRestrictions[key] = publicationRestrictions[key];
                    } else {
                        error(`There is issue restrictions which is not supported. key=${key}`);
                    }
                }
            });

            // config restrictions have more priority
            if (!configRestrictions.comments) {
                issueRestrictions.comments = true;
            }

            return issueRestrictions;
        }),
);

const selectIssueRestrictionsByCidDateKey = createSelector(
    selectIssueRestrictionsByCidDateKeySelector,
    selectCidDateKey,
    (getIssueRestrictionsByCidDateKey, cidDateKey) => {
        if (!cidDateKey) {
            return null;
        }

        return getIssueRestrictionsByCidDateKey(cidDateKey);
    },
);

const selectIsRadioAvailableForLanguage = createSelector(selectRadioAvailableLocales, selectIssueLanguage, (availableLanguages, language) => {
    return availableLanguages.indexOf(language) >= 0;
});

const selectIsRestrictedReadingMode = createSelector(
    selectIssueInfoByCidDateKey,
    selectIssueRestrictionsByCidDateKey,
    (issueInfoMeta, restrictions) => {
        if (!restrictions) {
            return false;
        }

        const issueInfo = issueInfoMeta?.info;
        if (!issueInfo) {
            return false;
        }

        return !issueInfo.purchaseInfo.isFreeContent && (restrictions.blackout || restrictions.smartFlowIfIssueCouldNotBeRead);
    },
);

const selectIsTextViewAvailableByCidDateKey = createSelector(
    selectIssueInfoByCidDateKey,
    selectIssueRestrictionsByCidDateKey,
    selectIsRestrictedReadingMode,
    (issueInfoMeta, restrictions, isRestrictedReadingMode) => {
        const issueInfo = issueInfoMeta?.info;
        if (!issueInfo) {
            return false;
        }

        if (isRestrictedReadingMode) {
            return false;
        }

        if (!issueInfo.couldBeRead) {
            if (!restrictions || restrictions.smartFlow || restrictions.smartFlowIfIssueCouldNotBeRead) {
                return false;
            }
        }

        return issueInfo.smartFlow.enabled && issueInfo.smartFlow.layoutValid;
    },
);

/** @deprecated Please use selectIsTextViewAvailableByCidDateKey instead. */
const selectIsSmartFlowAvailableByCidDateKey = selectIsTextViewAvailableByCidDateKey;

const selectIsRadioViewAvailableByCidDateKey = createSelector(
    selectIsRestrictedReadingMode,
    selectIssueRestrictionsByCidDateKey,
    selectIsRadioAvailableForLanguage,
    (isRestrictedReadingMode, restrictions, radioAvailableForLanguage) => {
        return !isRestrictedReadingMode && !restrictions?.sound && radioAvailableForLanguage;
    },
);

const selectIsPageViewAvailableByCidDateKey = createSelector(
    selectIssueInfoByCidDateKey,
    selectIssueRestrictionsByCidDateKey,
    selectIsRestrictedReadingMode,
    (issueInfoMeta, restrictions, isRestrictedReadingMode) => {
        if (!issueInfoMeta?.info) {
            return false;
        }

        if (restrictions?.replica) {
            return false;
        }

        return !isRestrictedReadingMode || !restrictions?.firstPageVisibilityIfIssueCouldNotBeRead;
    },
);

const selectIsBlackoutReadingMode = createSelector(selectIssueRestrictionsByCidDateKey, restrictions => !!restrictions?.blackout);

const selectIsSharingViaEmailAllowedByCidDateKey = createSelector(
    selectIssueRestrictionsByCidDateKey,
    restrictions => !(restrictions?.emailSharing ?? true),
);

const selectIsSharingViaSocialNetworksAllowedByCidDateKey = createSelector(
    selectIssueRestrictionsByCidDateKey,
    restrictions => !(restrictions?.blogging ?? true),
);

const selectIsIssueRestrictedForSharing = createSelector(
    selectIsSharingViaEmailAllowedByCidDateKey,
    selectIsSharingViaSocialNetworksAllowedByCidDateKey,
    (isEmailAllowed, isSocialNetworksAllowed) => {
        return !isEmailAllowed && !isSocialNetworksAllowed;
    },
);

export {
    selectIsBlackoutReadingMode,
    selectIsIssueRestrictedForSharing,
    selectIsPageViewAvailableByCidDateKey,
    selectIsRadioViewAvailableByCidDateKey,
    selectIsRestrictedReadingMode,
    selectIsSharingViaEmailAllowedByCidDateKey,
    selectIsSharingViaSocialNetworksAllowedByCidDateKey,
    selectIsSmartFlowAvailableByCidDateKey,
    selectIssueRestrictionsByCidDateKey,
    selectIssueRestrictionsByCidDateKeySelector,
    selectIssuesRestrictionsMap,
    selectIsTextViewAvailableByCidDateKey,
    selectPublicationsRestrictions,
};
